import { create } from "zustand";
import {
  BackendLineDirection,
  normalizeBackendDirection,
} from "../EditLineCrossingsUtils/lineDirection";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { AxiosError } from "axios";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import {
  getNumbersAfterEditCamera,
  getNumbersAfterSites,
} from "../../EditCamera/EditCamera";
import { LINE_COLORS } from "../EditLineCrossingsUtils/lineColors";
import {
  buildLineCrossingsListPath,
  lineCrossingsPresetSearch,
  parseLineCrossingsPresetIdFromHref,
} from "../EditLineCrossingsUtils/lineCrossingsListPath";

export type LineDirection = "AtoB" | "BtoA" | "Both";
export type { BackendLineDirection };

/**
 * Determines if Side A is on the left side of the screen based on line coordinates.
 * Side A is defined as clockwise from Point 1, Side B is clockwise from Point 2.
 *
 * For non-horizontal lines:
 * - If dy < 0 (point 2 above point 1): Side A is on the left
 * - If dy > 0 (point 2 below point 1): Side A is on the right
 *
 * For horizontal lines (dy = 0), the backend treats top as "left" and bottom as "right":
 * - If dx > 0 (point 2 to the right): Side A is below = right
 * - If dx < 0 (point 2 to the left): Side A is above = left
 */
const isSideAOnLeft = (
  coordinates: [[number, number], [number, number]]
): boolean => {
  const [point1, point2] = coordinates;
  const dx = point2[0] - point1[0];
  const dy = point2[1] - point1[1];

  if (dy !== 0) {
    // Non-horizontal line: Side A is left if point 2 is above point 1
    return dy < 0;
  } else {
    // Horizontal line: top = left, bottom = right
    // If dx > 0, Side A is below (right). If dx < 0, Side A is above (left).
    return dx < 0;
  }
};

/**
 * Converts backend direction format to frontend format.
 * Backend uses: "l2r" (left to right), "r2l" (right to left), "both"
 * Frontend uses: "AtoB", "BtoA", "Both"
 */
export const backendToFrontendDirection = (
  rawBackendDirection: string,
  coordinates: [[number, number], [number, number]]
): LineDirection => {
  // ENG-1317: case-insensitive; see normalizeBackendDirection.
  const backendDirection = normalizeBackendDirection(rawBackendDirection);
  if (backendDirection === "both") return "Both";

  const sideAIsLeft = isSideAOnLeft(coordinates);

  if (backendDirection === "l2r") {
    // Left to right crossing
    return sideAIsLeft ? "AtoB" : "BtoA";
  } else {
    // Right to left crossing (r2l)
    return sideAIsLeft ? "BtoA" : "AtoB";
  }
};

/**
 * Converts frontend direction format to backend format.
 * Frontend uses: "AtoB", "BtoA", "Both"
 * Backend uses: "l2r" (left to right), "r2l" (right to left), "both"
 */
export const frontendToBackendDirection = (
  frontendDirection: LineDirection,
  coordinates: [[number, number], [number, number]]
): BackendLineDirection => {
  if (frontendDirection === "Both") return "both";

  const sideAIsLeft = isSideAOnLeft(coordinates);

  if (frontendDirection === "AtoB") {
    // Crossing from A to B
    return sideAIsLeft ? "l2r" : "r2l";
  } else {
    // Crossing from B to A
    return sideAIsLeft ? "r2l" : "l2r";
  }
};

/**
 * Checks if a direction string is in the legacy frontend format.
 * Legacy format: "AtoB", "BtoA", "Both"
 * New backend format: "l2r", "r2l", "both"
 */
export const isLegacyDirectionFormat = (direction: string): boolean => {
  return direction === "AtoB" || direction === "BtoA" || direction === "Both";
};

/**
 * Converts a legacy frontend direction to the proper backend format.
 * This is used for migrating old data that was incorrectly stored.
 */
export const convertLegacyToBackendDirection = (
  legacyDirection: string,
  coordinates: [[number, number], [number, number]]
): BackendLineDirection => {
  if (legacyDirection === "Both") return "both";
  return frontendToBackendDirection(
    legacyDirection as LineDirection,
    coordinates
  );
};

/**
 * Canvas padding used by LineDrawer for visual spacing.
 * Coordinates in the UI include this padding, but backend stores percentages (0-1 range).
 */
export const CANVAS_PADDING = 20;

/**
 * Converts backend coordinates (percentages 0-1) to frontend coordinates (pixels with canvas padding).
 * Called when loading data from the API.
 * @param coordinates - Coordinates as percentages (0-1 range) from the API
 * @param width - Display width in pixels
 * @param height - Display height in pixels
 */
export const backendToFrontendCoordinates = (
  coordinates: [[number, number], [number, number]],
  width: number,
  height: number
): [[number, number], [number, number]] => {
  return [
    [
      coordinates[0][0] * width + CANVAS_PADDING,
      coordinates[0][1] * height + CANVAS_PADDING,
    ],
    [
      coordinates[1][0] * width + CANVAS_PADDING,
      coordinates[1][1] * height + CANVAS_PADDING,
    ],
  ];
};

/**
 * Converts frontend coordinates (pixels with canvas padding) to backend coordinates (percentages 0-1).
 * Called when saving data to the API.
 * @param coordinates - Coordinates as pixels with padding from the UI
 * @param width - Display width in pixels
 * @param height - Display height in pixels
 */
export const frontendToBackendCoordinates = (
  coordinates: [[number, number], [number, number]],
  width: number,
  height: number
): [[number, number], [number, number]] => {
  return [
    [
      (coordinates[0][0] - CANVAS_PADDING) / width,
      (coordinates[0][1] - CANVAS_PADDING) / height,
    ],
    [
      (coordinates[1][0] - CANVAS_PADDING) / width,
      (coordinates[1][1] - CANVAS_PADDING) / height,
    ],
  ];
};

export interface Line {
  id?: number; // Optional - new lines don't have an id, API assigns it
  camera: number;
  metric_labels: number[];
  coordinates: [[number, number], [number, number]];
  direction: LineDirection;
  /** Preset ("configuration") the line belongs to; null/undefined = camera-wide Default line. */
  preset?: number | null;
  color: string; // For rendering only, not sent to API
}

export interface MetricLabel {
  id: number;
  label_name: string;
}

/** One row of `GET /preset/?camera=<id>` — a named configuration a line can belong to. */
export interface LineCrossingPresetOption {
  id: number;
  name: string;
}

const parsePresetOptions = (data: unknown): LineCrossingPresetOption[] => {
  const rows: any[] = Array.isArray((data as any)?.results)
    ? (data as any).results
    : Array.isArray(data)
    ? (data as any[])
    : [];
  return rows
    .map((row) => ({ id: Number(row?.id), name: String(row?.name ?? "") }))
    .filter((row) => Number.isFinite(row.id) && row.id > 0)
    .map((row) => ({
      id: row.id,
      name: row.name.trim() || `Preset #${row.id}`,
    }));
};

/**
 * Map one `/line_crossing/` API row to the editor's `Line` shape: backend
 * percentage coordinates → canvas pixels (with padding), backend direction
 * (l2r/r2l/both, or a legacy AtoB/BtoA/Both value) → frontend direction.
 */
const toFrontendLine = (
  line: any,
  displayDimensions: { width: number; height: number },
  color: string
): Line => {
  const frontendCoordinates = backendToFrontendCoordinates(
    line.coordinates,
    displayDimensions.width,
    displayDimensions.height
  );
  let frontendDirection: LineDirection;
  if (isLegacyDirectionFormat(line.direction || "")) {
    // Legacy format - use as-is since it's already in frontend format
    frontendDirection = (line.direction as LineDirection) || "Both";
  } else {
    // New backend format - convert to frontend format
    frontendDirection = backendToFrontendDirection(
      (line.direction as BackendLineDirection) || "both",
      line.coordinates
    );
  }
  return {
    id: line.id,
    camera: line.camera,
    metric_labels: line.metric_labels || [],
    coordinates: frontendCoordinates,
    direction: frontendDirection,
    preset: line.preset ?? null,
    color,
  };
};

const resizeImageLineCrossings = (
  input: string
): { width: number; height: number } => {
  if (input) {
    const [widthStr, heightStr] = input
      .split("x")
      .map((str) => parseInt(str.trim()));
    const aspectRatio = widthStr / heightStr;

    let adjustedWidth = widthStr;
    let adjustedHeight = heightStr;

    if (adjustedWidth / aspectRatio > 360) {
      adjustedWidth = 640;
      adjustedHeight = adjustedWidth / aspectRatio;
    } else {
      adjustedHeight = 360;
      adjustedWidth = adjustedHeight * aspectRatio;
    }

    if (adjustedWidth > 640) {
      adjustedWidth = 640;
      adjustedHeight = adjustedWidth / aspectRatio;
    }

    if (adjustedHeight > 360) {
      adjustedHeight = 360;
      adjustedWidth = adjustedHeight * aspectRatio;
    }

    return {
      width: adjustedWidth,
      height: adjustedHeight,
    };
  } else {
    return {
      height: 360,
      width: 640,
    };
  }
};

interface EditLineCrossingsStore {
  // Lines array
  lines: Line[];
  setLines: (lines: Line[]) => void;

  // Original lines (snapshot when entering edit mode)
  originalLines: Line[];
  setOriginalLines: (originalLines: Line[]) => void;

  // Current line being drawn
  currentLine: Line | null;
  setCurrentLine: (line: Line | null) => void;

  // Drawing state
  isDrawing: boolean;
  setIsDrawing: (isDrawing: boolean) => void;

  // Selected line index
  selectedLineIndex: number | null;
  setSelectedLineIndex: (selectedLineIndex: number | null) => void;

  // Line color
  lineColor: string;
  setLineColor: (lineColor: string) => void;

  // Image dimensions
  imageWidth: number;
  imageHeight: number;
  setImageDimensions: (width: number, height: number) => void;

  // Adjusted dimensions for canvas
  adjustedDimensions: {
    x: number;
    y: number;
  };
  setAdjustedDimensions: (adjustedDimensions: { x: number; y: number }) => void;

  // Dimensions object (for image sizing)
  dimensions: {
    width: number;
    height: number;
  };
  setDimensions: (dimensions: { width: number; height: number }) => void;

  // Image URL
  imageURL: string;
  setImageURL: (imageURL: string) => void;

  // Page loading state
  pageLoading: boolean;
  setPageLoading: (pageLoading: boolean) => void;

  // Edits made flag
  editsMade: boolean;
  setEditsMade: (editsMade: boolean) => void;

  // Editor visible state
  editorVisible: boolean;
  setEditorVisible: (editorVisible: boolean) => void;

  // Breadcrumbs
  breadcrumbs: any[];
  setBreadcrumbs: (breadcrumbs: any[]) => void;

  // Camera info
  cameraInfo: any;
  setCameraInfo: (cameraInfo: any) => void;

  // Metric labels (filtered for intruder, vehicle, bike)
  metricLabels: MetricLabel[];
  setMetricLabels: (metricLabels: MetricLabel[]) => void;

  // Update line direction
  updateLineDirection: (lineIndex: number, direction: LineDirection) => void;

  // Update line metric labels
  updateLineMetricLabels: (lineIndex: number, metricLabels: number[]) => void;

  // Get all page info
  getAllEditLineCrossingsPageInfo: (
    navigate: NavigateFunction
  ) => Promise<void>;

  // Post/Patch line crossings
  handlePostLineCrossings: (navigate: NavigateFunction) => Promise<void>;

  // Delete line crossing dialog
  deleteLineCrossingDialogOpen: boolean;
  setDeleteLineCrossingDialogOpen: (open: boolean) => void;
  lineToDeleteId: number | null;
  setLineToDeleteId: (id: number | null) => void;

  // Delete line crossing
  handleDeleteLineCrossing: (navigate: NavigateFunction) => Promise<void>;

  // Line crossing active state (from general_info API)
  lineCrossingActive: { intruder: boolean; vehicle: boolean };
  setLineCrossingActive: (lineCrossingActive: {
    intruder: boolean;
    vehicle: boolean;
  }) => void;

  /**
   * Configuration being edited (ENG-1610): null = the camera-wide Default lines
   * (`preset__isnull=true`), otherwise one named preset's lines (`preset=<id>`).
   * Every fetch, refetch and POST on this page is scoped to it, so a Default
   * edit can never rewrite or delete a preset line and vice versa. Seeded from
   * the route's `?preset=<id>` on load.
   */
  selectedPresetId: number | null;
  /** Named presets for the camera (`GET /preset/?camera=`), for the configuration switcher. */
  cameraPresets: LineCrossingPresetOption[];
  /** Reload the line list for another configuration. No-op while the editor has unsaved edits open. */
  switchConfiguration: (
    navigate: NavigateFunction,
    presetId: number | null
  ) => Promise<void>;
}

export const useEditLineCrossingsStore = create<EditLineCrossingsStore>(
  (set, get) => ({
    // Lines array
    lines: [],
    setLines: (lines: Line[]) => set({ lines }),

    // Original lines
    originalLines: [],
    setOriginalLines: (originalLines: Line[]) => set({ originalLines }),

    // Current line being drawn
    currentLine: null,
    setCurrentLine: (currentLine: Line | null) => set({ currentLine }),

    // Drawing state
    isDrawing: false,
    setIsDrawing: (isDrawing: boolean) => set({ isDrawing }),

    // Selected line index
    selectedLineIndex: null,
    setSelectedLineIndex: (selectedLineIndex: number | null) =>
      set({ selectedLineIndex }),

    // Line color
    lineColor: "#FF8400",
    setLineColor: (lineColor: string) => set({ lineColor }),

    // Image dimensions
    imageWidth: 720,
    imageHeight: 576,
    setImageDimensions: (width: number, height: number) =>
      set({ imageWidth: width, imageHeight: height }),

    // Adjusted dimensions
    adjustedDimensions: {
      x: 720,
      y: 576,
    },
    setAdjustedDimensions: (adjustedDimensions: { x: number; y: number }) =>
      set({ adjustedDimensions }),

    // Dimensions object
    dimensions: {
      width: 640,
      height: 360,
    },
    setDimensions: (dimensions: { width: number; height: number }) =>
      set({ dimensions }),

    // Image URL
    imageURL: "",
    setImageURL: (imageURL: string) => set({ imageURL }),

    // Page loading state
    pageLoading: true,
    setPageLoading: (pageLoading: boolean) => set({ pageLoading }),

    // Edits made flag
    editsMade: false,
    setEditsMade: (editsMade: boolean) => set({ editsMade }),

    // Editor visible state
    editorVisible: false,
    setEditorVisible: (editorVisible: boolean) => set({ editorVisible }),

    // Breadcrumbs
    breadcrumbs: [],
    setBreadcrumbs: (breadcrumbs: any[]) => set({ breadcrumbs }),

    // Camera info
    cameraInfo: {
      camera_name: "",
    },
    setCameraInfo: (cameraInfo: any) => set({ cameraInfo }),

    // Metric labels
    metricLabels: [],
    setMetricLabels: (metricLabels: MetricLabel[]) => set({ metricLabels }),

    // Update line direction
    updateLineDirection: (lineIndex: number, direction: LineDirection) => {
      const { lines } = get();
      const updatedLines = [...lines];
      if (updatedLines[lineIndex]) {
        updatedLines[lineIndex] = {
          ...updatedLines[lineIndex],
          direction,
        };
        set({ lines: updatedLines, editsMade: true });
      }
    },

    // Update line metric labels
    updateLineMetricLabels: (lineIndex: number, metricLabels: number[]) => {
      const { lines } = get();
      const updatedLines = [...lines];
      if (updatedLines[lineIndex]) {
        updatedLines[lineIndex] = {
          ...updatedLines[lineIndex],
          metric_labels: metricLabels,
        };
        set({ lines: updatedLines, editsMade: true });
      }
    },

    // Get all page info
    getAllEditLineCrossingsPageInfo: async (navigate: NavigateFunction) => {
      const {
        getAllEditLineCrossingsPageInfo,
        setDimensions,
        setImageURL,
        setPageLoading,
        setBreadcrumbs,
        setCameraInfo,
        setMetricLabels,
        setLines,
        setOriginalLines,
        setLineCrossingActive,
      } = get();
      const AxiosUI = createApi(``);
      const cameraId: any = getNumbersAfterEditCamera(location.href);
      // `?preset=<id>` pre-selects a configuration (Site About passes its picker).
      const requestedPresetId = parseLineCrossingsPresetIdFromHref(location.href);
      const retryFunction = () => {
        getAllEditLineCrossingsPageInfo(navigate);
      };

      try {
        // Run API calls concurrently using Promise.all. The preset list is
        // optional context for the configuration switcher, so its failure must
        // not take the page down with it.
        const [
          cameraStatusResponse,
          customerAboutResponse,
          metricsResponse,
          lineCrossingsResponse,
          generalInfoResponse,
          presetsResponse,
        ] = await Promise.all([
          AxiosUI.get(`/camera/${cameraId}/status/`),
          AxiosUI.get(
            `/customer/${getNumbersAfterSites(location.href)}/about/`
          ),
          AxiosUI.get(`/metric/?camera_id=${cameraId}`),
          AxiosUI.get(buildLineCrossingsListPath(cameraId, requestedPresetId)),
          AxiosUI.get(`/camera/${cameraId}/general_info/`),
          AxiosUI.get(`/preset/?camera=${cameraId}`).catch(() => null),
        ]);

        const cameraPresets = presetsResponse
          ? parsePresetOptions(presetsResponse.data)
          : [];
        // Keep a requested preset even if the list could not be loaded (the
        // lines above were already fetched for it); drop it only when the
        // list loaded and proves it is not one of this camera's presets.
        const selectedPresetId =
          requestedPresetId != null &&
          (presetsResponse == null ||
            cameraPresets.some((preset) => preset.id === requestedPresetId))
            ? requestedPresetId
            : null;
        set({ cameraPresets, selectedPresetId });
        // The lines above were fetched for the requested preset. If that
        // preset was just rejected (deleted, or another camera's id in a
        // stale link) the page is now showing Default, so it needs Default's
        // lines rather than the rejected preset's (usually empty) list.
        const linesResponse =
          selectedPresetId === requestedPresetId
            ? lineCrossingsResponse
            : await AxiosUI.get(buildLineCrossingsListPath(cameraId, null));

        // Process all responses
        const displayDimensions = resizeImageLineCrossings(
          cameraStatusResponse.data.frame_dimensions
        );
        setDimensions(displayDimensions);
        setCameraInfo(cameraStatusResponse.data);
        setImageURL(cameraStatusResponse.data.preview_url);
        setBreadcrumbs(customerAboutResponse.data.breadcrumbs);

        // Filter metrics to only include intruder_line_crossing, vehicle_line_crossing, bike_line_crossing
        const allowedLabels = [
          "intruder_line_crossing",
          "vehicle_line_crossing",
          "bike_line_crossing",
        ];
        const filteredMetrics = metricsResponse.data.results
          .filter((metric: any) => allowedLabels.includes(metric.label_name))
          .map((metric: any) => ({
            id: metric.label,
            label_name: metric.label_name,
          }));
        setMetricLabels(filteredMetrics);

        // Extract lineCrossingActive state from general_info response
        const streams = generalInfoResponse.data.streams || [];
        let intruderLineCrossingActive = false;
        let vehicleLineCrossingActive = false;

        streams.forEach((stream: any) => {
          const streamMetrics = stream.stream_metrics || [];
          streamMetrics.forEach((metric: any) => {
            if (
              metric.label_name === "intruder_line_crossing" &&
              metric.active
            ) {
              intruderLineCrossingActive = true;
            }
            if (
              metric.label_name === "vehicle_line_crossing" &&
              metric.active
            ) {
              vehicleLineCrossingActive = true;
            }
          });
        });

        setLineCrossingActive({
          intruder: intruderLineCrossingActive,
          vehicle: vehicleLineCrossingActive,
        });

        // Process line crossings - assign colors to each line
        // Convert backend direction format (l2r/r2l/both) to frontend format (AtoB/BtoA/Both)
        const lineCrossings = linesResponse.data.results || [];

        // Check for legacy direction formats and migrate them
        const legacyLines = lineCrossings.filter(
          (line: any) =>
            line.direction && isLegacyDirectionFormat(line.direction)
        );

        if (legacyLines.length > 0) {
          // Migrate legacy directions to new format by patching them
          const migrationPromises = legacyLines.map((line: any) => {
            const correctedDirection = convertLegacyToBackendDirection(
              line.direction,
              line.coordinates
            );
            return AxiosUI.patch(`/line_crossing/${line.id}/`, {
              direction: correctedDirection,
            });
          });

          // Wait for all migrations to complete (fire and forget - don't block UI)
          Promise.all(migrationPromises).catch((error) => {
            console.warn(
              "Failed to migrate some legacy line crossing directions:",
              error
            );
          });
        }

        const processedLines: Line[] = lineCrossings.map(
          (line: any, index: number) =>
            toFrontendLine(
              line,
              displayDimensions,
              LINE_COLORS[index % LINE_COLORS.length]
            )
        );
        setLines(processedLines);
        setOriginalLines(processedLines);

        // Set loading to false only after all requests complete successfully
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert(
              "Unable to load page information. Please try refreshing the page."
            );
            setPageLoading(false);
          }
        );
      }
    },

    selectedPresetId: null,
    cameraPresets: [],
    switchConfiguration: async (navigate: NavigateFunction, presetId: number | null) => {
      const {
        editorVisible,
        dimensions,
        setPageLoading,
        setLines,
        setOriginalLines,
        setSelectedLineIndex,
      } = get();
      if (editorVisible) {
        // Unsaved edits belong to the current configuration; the page keeps the
        // switcher disabled in edit mode, this is the belt to that braces.
        return;
      }
      const cameraId = getNumbersAfterEditCamera(location.href);
      const retryFunction = () => {
        get().switchConfiguration(navigate, presetId);
      };
      const AxiosUI = createApi(``);
      // Commit the new configuration only once its lines are in hand; until
      // then the lines on screen still belong to the previous one, and a
      // save against a mislabeled list would write into the wrong configuration.
      const previousPresetId = get().selectedPresetId;
      set({ pageLoading: true });
      try {
        const lineCrossingsResponse = await AxiosUI.get(
          buildLineCrossingsListPath(cameraId, presetId)
        );
        const lineCrossings = lineCrossingsResponse.data.results || [];
        const processedLines: Line[] = lineCrossings.map(
          (line: any, index: number) =>
            toFrontendLine(line, dimensions, LINE_COLORS[index % LINE_COLORS.length])
        );
        set({ selectedPresetId: presetId });
        setLines(processedLines);
        setOriginalLines(processedLines);
        setSelectedLineIndex(null);
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert(
              "Unable to load line crossings for this configuration. Please try again."
            );
            // The page rewrote the URL before calling us; put it back so a
            // refresh reopens the configuration whose lines are still shown.
            navigate(
              { search: lineCrossingsPresetSearch(previousPresetId) },
              { replace: true }
            );
            setPageLoading(false);
          }
        );
      }
    },

    // Post/Patch line crossings
    handlePostLineCrossings: async (navigate: NavigateFunction) => {
      const {
        handlePostLineCrossings,
        lines,
        originalLines,
        dimensions,
        selectedPresetId,
        setPageLoading,
        setLines,
        setOriginalLines,
        setEditorVisible,
        setEditsMade,
      } = get();
      const AxiosUI = createApi(``);
      const cameraId = getNumbersAfterEditCamera(location.href);
      const retryFunction = () => {
        handlePostLineCrossings(navigate);
      };

      try {
        // Find lines that were deleted during edit mode
        // (lines that existed in originalLines but are no longer in lines)
        const currentLineIds = new Set(lines.map((l) => l.id).filter(Boolean));
        const deletedLines = originalLines.filter(
          (ol) => ol.id && !currentLineIds.has(ol.id)
        );

        // Create array of promises for DELETE calls
        const deletePromises = deletedLines.map((line) =>
          AxiosUI.delete(`/line_crossing/${line.id}/`)
        );

        // Create array of promises for POST/PATCH calls
        const apiPromises = lines.map((line: Line) => {
          // Convert frontend coordinates (pixels with padding) to backend coordinates (percentages)
          const backendCoordinates = frontendToBackendCoordinates(
            line.coordinates,
            dimensions.width,
            dimensions.height
          );

          // Prepare line data for API (exclude color which is for rendering only)
          // Convert frontend direction format (AtoB/BtoA/Both) to backend format (l2r/r2l/both)
          // Use backend coordinates for direction calculation since that's what the backend uses
          const lineData = {
            camera: line.camera,
            metric_labels: line.metric_labels,
            coordinates: backendCoordinates,
            direction: frontendToBackendDirection(
              line.direction,
              backendCoordinates
            ),
          };

          if (!line.id) {
            // New line - POST without id. A line created while a named
            // configuration is loaded belongs to that preset; Default lines
            // omit the field (the API defaults `preset` to null).
            return AxiosUI.post(
              `/line_crossing/`,
              selectedPresetId != null
                ? { ...lineData, preset: selectedPresetId }
                : lineData
            );
          } else {
            // Existing line - check if it was modified
            const originalLine = originalLines.find((ol) => ol.id === line.id);
            const wasModified =
              !originalLine ||
              JSON.stringify({
                metric_labels: originalLine.metric_labels,
                coordinates: originalLine.coordinates,
                direction: originalLine.direction,
              }) !==
                JSON.stringify({
                  metric_labels: line.metric_labels,
                  coordinates: line.coordinates,
                  direction: line.direction,
                });

            if (wasModified) {
              // Modified existing line - PATCH with id
              return AxiosUI.patch(`/line_crossing/${line.id}/`, lineData);
            }
            // No changes to this line - return resolved promise
            return Promise.resolve();
          }
        });

        // Wait for all API calls to complete (deletes + posts/patches)
        await Promise.all([...deletePromises, ...apiPromises]);

        // Saving no longer auto-syncs. Mark the site as having unsynced
        // changes; the operator syncs explicitly via the edit-view Sync control.
        markSiteUnsynced();

        // Fetch updated lines from API to get proper IDs (same configuration)
        const lineCrossingsResponse = await AxiosUI.get(
          buildLineCrossingsListPath(cameraId, selectedPresetId)
        );
        const lineCrossings = lineCrossingsResponse.data.results || [];

        // Build a map of existing line colors to preserve them
        // For existing lines, use their ID; for new lines, use backend coordinates as key
        const existingColorMap = new Map<string, string>();
        lines.forEach((line) => {
          if (line.id) {
            existingColorMap.set(`id:${line.id}`, line.color);
          }
          // Also map by backend coordinates (percentages) for newly created lines
          const backendCoords = frontendToBackendCoordinates(
            line.coordinates,
            dimensions.width,
            dimensions.height
          );
          const coordKey = `coords:${JSON.stringify(backendCoords)}`;
          existingColorMap.set(coordKey, line.color);
        });

        const processedLines: Line[] = lineCrossings.map(
          (line: any, index: number) => {
            // Try to find existing color by ID first, then by coordinates
            const existingColorById = existingColorMap.get(`id:${line.id}`);
            const existingColorByCoords = existingColorMap.get(
              `coords:${JSON.stringify(line.coordinates)}`
            );
            const existingColor = existingColorById || existingColorByCoords;

            // Convert backend coordinates (percentages) to frontend coordinates (pixels with padding)
            const frontendCoordinates = backendToFrontendCoordinates(
              line.coordinates,
              dimensions.width,
              dimensions.height
            );

            // Handle both legacy and new formats
            let frontendDirection: LineDirection;
            if (isLegacyDirectionFormat(line.direction || "")) {
              // Legacy format - use as-is since it's already in frontend format
              frontendDirection = (line.direction as LineDirection) || "Both";
            } else {
              // New backend format - convert to frontend format
              frontendDirection = backendToFrontendDirection(
                (line.direction as BackendLineDirection) || "both",
                line.coordinates
              );
            }

            return {
              id: line.id,
              camera: line.camera,
              metric_labels: line.metric_labels || [],
              coordinates: frontendCoordinates,
              direction: frontendDirection,
              // Preserve existing color if available, otherwise assign new one
              color: existingColor || LINE_COLORS[index % LINE_COLORS.length],
            };
          }
        );
        setLines(processedLines);
        setOriginalLines(processedLines);

        // Exit edit mode
        setEditorVisible(false);
        setEditsMade(false);

        // Set loading to false only after all requests complete successfully
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Unable to save line crossings. Please try again.");
            // Reset lines to original state (like cancel)
            set({ lines: [...originalLines] });
            setEditorVisible(false);
            setEditsMade(false);
            setPageLoading(false);
          }
        );
      }
    },

    // Delete line crossing dialog
    deleteLineCrossingDialogOpen: false,
    setDeleteLineCrossingDialogOpen: (open: boolean) =>
      set({ deleteLineCrossingDialogOpen: open }),
    lineToDeleteId: null,
    setLineToDeleteId: (id: number | null) => set({ lineToDeleteId: id }),

    // Line crossing active state
    lineCrossingActive: { intruder: false, vehicle: false },
    setLineCrossingActive: (lineCrossingActive: {
      intruder: boolean;
      vehicle: boolean;
    }) => set({ lineCrossingActive }),

    // Delete line crossing
    handleDeleteLineCrossing: async (navigate: NavigateFunction) => {
      const {
        handleDeleteLineCrossing,
        lines: currentLines,
        dimensions,
        lineToDeleteId,
        setPageLoading,
        setLines,
        setOriginalLines,
        setLineToDeleteId,
        setDeleteLineCrossingDialogOpen,
      } = get();
      const AxiosUI = createApi(``);
      const cameraId = getNumbersAfterEditCamera(location.href);
      const retryFunction = () => {
        handleDeleteLineCrossing(navigate);
      };

      if (!lineToDeleteId) {
        setDeleteLineCrossingDialogOpen(false);
        setPageLoading(false);
        return;
      }

      try {
        // Delete the line crossing
        await AxiosUI.delete(`/line_crossing/${lineToDeleteId}/`);

        // Deleting a line crossing no longer auto-syncs. Mark the site as
        // having unsynced changes; the operator syncs explicitly via the
        // edit-view Sync control.
        markSiteUnsynced();

        // Fetch updated lines (same configuration)
        const lineCrossingsResponse = await AxiosUI.get(
          buildLineCrossingsListPath(cameraId, get().selectedPresetId)
        );
        const lineCrossings = lineCrossingsResponse.data.results || [];

        // Build a map of existing line colors to preserve them
        const existingColorMap = new Map<number, string>();
        currentLines.forEach((line) => {
          if (line.id) {
            existingColorMap.set(line.id, line.color);
          }
        });

        const processedLines: Line[] = lineCrossings.map(
          (line: any, index: number) => {
            // Convert backend coordinates (percentages) to frontend coordinates (pixels with padding)
            const frontendCoordinates = backendToFrontendCoordinates(
              line.coordinates,
              dimensions.width,
              dimensions.height
            );

            // Handle both legacy and new formats
            let frontendDirection: LineDirection;
            if (isLegacyDirectionFormat(line.direction || "")) {
              // Legacy format - use as-is since it's already in frontend format
              frontendDirection = (line.direction as LineDirection) || "Both";
            } else {
              // New backend format - convert to frontend format
              frontendDirection = backendToFrontendDirection(
                (line.direction as BackendLineDirection) || "both",
                line.coordinates
              );
            }

            return {
              id: line.id,
              camera: line.camera,
              metric_labels: line.metric_labels || [],
              coordinates: frontendCoordinates,
              direction: frontendDirection,
              // Preserve existing color if available, otherwise assign new one
              color:
                existingColorMap.get(line.id) ||
                LINE_COLORS[index % LINE_COLORS.length],
            };
          }
        );
        setLines(processedLines);
        setOriginalLines(processedLines);

        // Reset delete state
        setLineToDeleteId(null);
        setDeleteLineCrossingDialogOpen(false);

        // Set loading to false only after all requests complete successfully
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Unable to delete line crossing. Please try again.");
            setDeleteLineCrossingDialogOpen(false);
            setPageLoading(false);
          }
        );
      }
    },
  })
);
